import { useEditCanvasStore } from '../../store';
import { type TextData, type TextNodeData } from '../../types';
import { isTextNode } from '../../utils/typeGuards';
import { DEFAULT_FONT_SIZE } from './constants';

/**
 * Builds a TextNodeData payload from local TextData, applying optional overrides.
 */
export function buildTextNodeData(data: TextData, overrides?: Partial<TextData>): TextNodeData {
  const merged = overrides ? { ...data, ...overrides } : data;

  const base: Omit<TextNodeData, 'fontSource' | 'fontFileUrl' | 'fontMetadataUrl'> = {
    fontFamily: merged.fontFamily ?? 'Inter',
    fontSize: merged.fontSize ?? DEFAULT_FONT_SIZE,
    source: merged.source ?? 'manual-edit',
    text: merged.text ?? '',
    type: 'text',
    ...(merged.fontColor && { fontColor: merged.fontColor }),
    ...(merged.fontVariant && { fontVariant: merged.fontVariant }),
    ...(merged.textAlign && { textAlign: merged.textAlign }),
    ...(merged.letterSpacing && { letterSpacing: merged.letterSpacing }),
    ...(merged.lineHeight && { lineHeight: merged.lineHeight }),
    ...(merged.prompt && { prompt: merged.prompt }),
  };

  if (merged.fontSource === 'file' && merged.fontFileUrl) {
    return { ...base, fontFileUrl: merged.fontFileUrl, fontSource: 'file' };
  }

  return {
    ...base,
    fontSource: merged.fontSource === 'file' ? undefined : merged.fontSource,
    ...(merged.fontMetadataUrl ? { fontMetadataUrl: merged.fontMetadataUrl } : {}),
  };
}

/**
 * Syncs a text node to the server: updates if it has a remoteId, creates otherwise.
 */
export function syncTextNode(nodeId: string, overrides?: Partial<TextData>) {
  const node = useEditCanvasStore.getState().nodes.find((n) => n.id === nodeId);

  if (!isTextNode(node)) {
    return;
  }

  if (node.remoteId) {
    useEditCanvasStore.getState().updateNodes?.([
      {
        data: buildTextNodeData(node.data, overrides),
        id: nodeId,
      },
    ]);
  } else {
    useEditCanvasStore.getState().createNodes?.([node]);
  }
}
